import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationAPI } from '../../services/api';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { sanitizeHtml } from '../../utils/sanitize';
import { sanitizeTextOnInput, validateTextForSubmission } from '../../utils/textValidation';

/** Flatten nested variables object into list of { name, description } for display */
function flattenVariables(
  obj: Record<string, unknown>,
  prefix = ''
): { name: string; description: string }[] {
  const result: { name: string; description: string }[] = [];
  if (!obj || typeof obj !== 'object') return result;
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const firstVal = Object.values(value)[0];
      if (typeof firstVal === 'string') {
        for (const [k, v] of Object.entries(value as Record<string, string>)) {
          result.push({ name: `${fullKey}.${k}`, description: String(v) });
        }
      } else {
        result.push(...flattenVariables(value as Record<string, unknown>, fullKey));
      }
    } else {
      result.push({ name: fullKey, description: String(value ?? '') });
    }
  }
  return result;
}

interface SurveyEmailTemplate {
  id: string;
  name: string;
  description?: string;
  subject: string;
  htmlBody: string;
  textBody: string;
  isActive: boolean;
  variables?: any;
  createdAt: string;
  updatedAt: string;
  usedInSurveyTemplates?: string[]; // Array of survey template names that use this email template
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SurveyEmailTemplatesManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [allTemplates, setAllTemplates] = useState<SurveyEmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterActive, setFilterActive] = useState<string>('all');

  // Create/Edit Modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SurveyEmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    htmlBody: '',
    textBody: '',
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<{ name?: string; description?: string }>({});

  // Preview Modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<SurveyEmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<SurveyEmailTemplate | null>(null);

  // Variable List (create/edit modal) - show available template variables
  const [showVariableList, setShowVariableList] = useState(false);

  // Survey Result Modal (for error messages from delete API)
  const [surveyResultModal, setSurveyResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    isOpen: false,
    type: 'error',
    message: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, [filterActive]);

  // Fetch available variables when create/edit modal is open (for Variable List)
  const { data: variablesResponse } = useQuery({
    queryKey: ['survey-email-templates-variables'],
    queryFn: async () => {
      const res = await notificationAPI.surveyEmailTemplates.variables();
      return res.data;
    },
    enabled: showFormModal,
    staleTime: 5 * 60 * 1000,
  });

  const variableList = useMemo(() => {
    const raw = variablesResponse?.data ?? variablesResponse;
    if (!raw || typeof raw !== 'object') return [];
    return flattenVariables(raw as Record<string, unknown>);
  }, [variablesResponse]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterActive, pageSize]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterActive !== 'all') params.isActive = filterActive === 'active';

      const response = await notificationAPI.surveyEmailTemplates.list(params);
      setAllTemplates(response.data.data || response.data || []);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch templates');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side pagination
  const totalCount = allTemplates.length;
  const templates = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return allTemplates.slice(startIndex, startIndex + pageSize);
  }, [allTemplates, currentPage, pageSize]);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      subject: '',
      htmlBody: '',
      textBody: '',
      isActive: true,
    });
    setFormErrors({}); // Clear any previous errors
    setShowFormModal(true);
  };

  const handleEdit = (template: SurveyEmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody,
      isActive: template.isActive,
    });
    setFormErrors({}); // Clear any previous errors
    setShowFormModal(true);
  };

  const handleSave = async () => {
    // Clear previous errors
    setFormErrors({});

    // Validate template name
    const nameValidation = validateTextForSubmission(formData.name, 'Template name', true);
    if (!nameValidation.isValid) {
      setFormErrors({ name: nameValidation.error || 'Template name is required' });
      showError(nameValidation.error || 'Please fix the validation errors.');
      return;
    }

    // Validate description (optional field)
    if (formData.description && formData.description.trim()) {
      const descriptionValidation = validateTextForSubmission(formData.description, 'Description', false);
      if (!descriptionValidation.isValid) {
        setFormErrors({ description: descriptionValidation.error || '' });
        showError(descriptionValidation.error || 'Please fix the validation errors.');
        return;
      }
    }

    // Basic validation for other required fields
    if (!formData.subject?.trim() || !formData.htmlBody?.trim() || !formData.textBody?.trim()) {
      showError('Please fill in all required fields (Name, Subject, HTML Body, and Text Body).');
      return;
    }

    try {
      setSaving(true);
      if (editingTemplate) {
        await notificationAPI.surveyEmailTemplates.update(editingTemplate.id, formData);
        showSuccess('Template updated successfully');
      } else {
        await notificationAPI.surveyEmailTemplates.create(formData);
        showSuccess('Template created successfully');
      }
      setShowFormModal(false);
      setFormErrors({}); // Clear errors on successful save
      fetchTemplates();
      // Invalidate React Query cache so Survey Template Form shows the new/updated template
      await queryClient.invalidateQueries({ queryKey: ['survey-email-templates'] });
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save template');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTemplate) return;

    try {
      await notificationAPI.surveyEmailTemplates.delete(deletingTemplate.id);
      showSuccess('Template deleted successfully');
      setShowDeleteModal(false);
      setDeletingTemplate(null);
      fetchTemplates();
      // Invalidate React Query cache so Survey Template Form reflects the deletion
      await queryClient.invalidateQueries({ queryKey: ['survey-email-templates'] });
    } catch (error: any) {
      // Close delete confirmation modal first
      setShowDeleteModal(false);
      setDeletingTemplate(null);
      
      // Show error in Survey-style popup modal
      const errorMessage = error.response?.data?.message || 'Failed to delete template';
      setSurveyResultModal({
        isOpen: true,
        type: 'error',
        message: errorMessage
      });
      console.error('Error:', error);
    }
  };

  const handlePreview = async (template: SurveyEmailTemplate) => {
    setPreviewTemplate(template);
    setShowPreviewModal(true);
    setPreviewLoading(true);

    try {
      // Sample data for survey email preview
      const sampleData = {
        surveyId: 'survey-123',
        surveyNumber: 'SUR0000123',
        shortDescription: 'Customer Satisfaction Survey',
        respondentEmail: 'customer@example.com',
        respondentName: 'John Doe',
        surveyUrl: `${window.location.origin}/surveys/survey-123/respond`,
        questions: [
          { question: 'How satisfied were you with our service?', type: 'rating', required: true },
          { question: 'Would you recommend us to others?', type: 'yesno', required: true },
          { question: 'Any additional comments?', type: 'text', required: false },
        ],
        companyName: 'ITSM Platform',
      };

      const response = await notificationAPI.surveyEmailTemplates.preview(template.id, sampleData);
      setPreviewHtml(response.data.data.htmlBody);
    } catch (error: any) {
      showError('Failed to generate preview');
      console.error('Error:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  // Calculate pagination values
  const totalPages = Math.ceil(totalCount / pageSize);
  const startEntry = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalCount);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <h1 className="text-xl font-semibold text-gray-900">Survey Email Templates Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure email templates for survey invitations sent to respondents.
          </p>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-2 p-3 rounded-md ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
        <div className="flex items-center justify-between">
          <div>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleCreateNew}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Template
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new survey email template.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Used In Survey Template
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 line-clamp-1">{template.name}</div>
                    {template.description && (
                      <div className="text-xs text-gray-500 line-clamp-1">{template.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 line-clamp-1" title={template.subject}>{template.subject}</div>
                  </td>
                  <td className="px-4 py-3">
                    {template.usedInSurveyTemplates && template.usedInSurveyTemplates.length > 0 ? (
                      <div className="text-sm text-gray-900" title={template.usedInSurveyTemplates.join(', ')}>
                        <ul>
                          {template.usedInSurveyTemplates.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400">-</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {template.isActive ? (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handlePreview(template)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Preview"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingTemplate(template);
                          setShowDeleteModal(true);
                        }}
                        title="Delete"
                        disabled={template.usedInSurveyTemplates && template.usedInSurveyTemplates.length > 0}
                        className={`p-1 ${template.usedInSurveyTemplates && template.usedInSurveyTemplates.length > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
        )}
        </div>

        {/* Enhanced Pagination */}
        {!loading && templates.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex-1 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="pageSize" className="text-sm text-gray-700">
                    Show
                  </label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="text-sm text-gray-700">
                  Showing {startEntry} to {endEntry} of {totalCount} entries
                </div>
              </div>

              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">First</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M15.79 14.77a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L11.832 10l3.938 3.71a.75.75 0 01.02 1.06zm-6 0a.75.75 0 01-1.06.02l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 111.04 1.08L5.832 10l3.938 3.71a.75.75 0 01.02 1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {getPageNumbers().map((page, index) => (
                    <React.Fragment key={index}>
                      {page === '...' ? (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      ) : (
                        <button
                          onClick={() => setCurrentPage(page as number)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )}
                    </React.Fragment>
                  ))}

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>

                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Last</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.21 14.77a.75.75 0 01.02-1.06L14.168 10 10.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02zm-6 0a.75.75 0 01.02-1.06L8.168 10 4.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Transition appear show={showFormModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          setShowFormModal(false);
          setFormErrors({}); // Clear errors when modal closes
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {editingTemplate ? 'Edit Survey Email Template' : 'Create Survey Email Template'}
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        setShowFormModal(false);
                        setShowVariableList(false);
                        setFormErrors({}); // Clear errors when modal closes
                      }}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Template Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => {
                            // Sanitize input in real-time to prevent HTML tags and dangerous characters
                            const sanitized = sanitizeTextOnInput(e.target.value);
                            setFormData({ ...formData, name: sanitized });
                            // Clear error when user starts typing
                            if (formErrors.name) {
                              setFormErrors({ ...formErrors, name: undefined });
                            }
                          }}
                          className={`mt-1 block w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                            formErrors.name 
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                              : 'border-gray-300'
                          }`}
                          placeholder="e.g., Default Survey Invitation"
                        />
                        {formErrors.name && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => {
                          // Sanitize input in real-time to prevent HTML tags and dangerous characters
                          const sanitized = sanitizeTextOnInput(e.target.value);
                          setFormData({ ...formData, description: sanitized });
                          // Clear error when user starts typing
                          if (formErrors.description) {
                            setFormErrors({ ...formErrors, description: undefined });
                          }
                        }}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                          formErrors.description 
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Brief description of this template"
                      />
                      {formErrors.description && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                      )}
                    </div>

                    {/* Variable List - click to show available template variables */}
                    <div className="border border-gray-200 rounded-lg bg-gray-50/50">
                      <button
                        type="button"
                        onClick={() => setShowVariableList((v) => !v)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                          Variable List
                        </span>
                        {showVariableList ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {showVariableList && (
                        <div className="px-4 pb-4 pt-0 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mt-2 mb-3">
                            Use in template as {`{{variableName}}`} (e.g. {`{{publicLink}}`}, {`{{caseRecord.number}}`})
                          </p>
                          <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                            {variableList.length === 0 ? (
                              <p className="text-sm text-gray-500">Loading variables...</p>
                            ) : (
                              variableList.map((v) => (
                                <div
                                  key={v.name}
                                  className="flex gap-3 py-1.5 px-2 rounded bg-white border border-gray-100 text-sm"
                                >
                                  <code className="shrink-0 font-mono text-primary-600 font-medium whitespace-nowrap">
                                    {`{{${v.name}}}`}
                                  </code>
                                  <span className="text-gray-600">{v.description}</span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="e.g., Survey Request: {{shortDescription}}"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use Handlebars syntax for variables: {`{{surveyNumber}}, {{shortDescription}}, {{respondentName}}, etc.`}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HTML Body <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <CodeMirror
                          value={formData.htmlBody}
                          height="400px"
                          extensions={[html()]}
                          onChange={(value) => setFormData({ ...formData, htmlBody: value })}
                          theme="light"
                          basicSetup={{
                            lineNumbers: true,
                            highlightActiveLineGutter: true,
                            highlightSpecialChars: true,
                            foldGutter: true,
                            drawSelection: true,
                            dropCursor: true,
                            allowMultipleSelections: true,
                            indentOnInput: true,
                            bracketMatching: true,
                            closeBrackets: true,
                            autocompletion: true,
                            rectangularSelection: true,
                            crosshairCursor: true,
                            highlightActiveLine: true,
                            highlightSelectionMatches: true,
                            closeBracketsKeymap: true,
                            searchKeymap: true,
                            foldKeymap: true,
                            completionKeymap: true,
                            lintKeymap: true,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Use Handlebars syntax for variables: {`{{surveyNumber}}, {{shortDescription}}, {{respondentName}}, {{surveyUrl}}, etc.`}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Text Body (Plain Text) <span className="text-red-500">*</span>
                      </label>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <CodeMirror
                          value={formData.textBody}
                          height="350px"
                          onChange={(value) => setFormData({ ...formData, textBody: value })}
                          theme="light"
                          placeholder="Plain text version of your email template..."
                          basicSetup={{
                            lineNumbers: true,
                            highlightActiveLineGutter: true,
                            highlightSpecialChars: true,
                            foldGutter: true,
                            drawSelection: true,
                            dropCursor: true,
                            allowMultipleSelections: true,
                            indentOnInput: true,
                            bracketMatching: true,
                            closeBrackets: true,
                            autocompletion: true,
                            rectangularSelection: true,
                            crosshairCursor: true,
                            highlightActiveLine: true,
                            highlightSelectionMatches: true,
                            closeBracketsKeymap: true,
                            searchKeymap: true,
                            foldKeymap: true,
                            completionKeymap: true,
                            lintKeymap: true,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Plain text version for email clients that don't support HTML
                      </p>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Active (template will be available for survey templates)
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowFormModal(false);
                        setShowVariableList(false);
                        setFormErrors({});
                      }}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-300"
                    >
                      {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Preview Modal */}
      <Transition appear show={showPreviewModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowPreviewModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Template Preview: {previewTemplate?.name}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowPreviewModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {previewLoading ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    ) : (
                      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-[70vh] overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) }} />
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPreviewModal(false)}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Delete Survey Email Template
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the template <strong>{deletingTemplate?.name}</strong>?
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingTemplate(null);
                      }}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Survey Result Modal (for error messages from delete API) */}
      <Transition appear show={surveyResultModal.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setSurveyResultModal({ isOpen: false, type: 'error', message: '' })}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {surveyResultModal.type === 'success' ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        ) : (
                          <XCircleIcon className="h-6 w-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                          {surveyResultModal.type === 'success' ? 'Success' : 'Error'}
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      onClick={() => setSurveyResultModal({ isOpen: false, type: 'error', message: '' })}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-2 ml-9">
                    <p className="text-sm text-gray-500">
                      {surveyResultModal.message}
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setSurveyResultModal({ isOpen: false, type: 'error', message: '' })}
                      className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                        surveyResultModal.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default SurveyEmailTemplatesManagement;

